/**
 * AI-suggested social share copy for the in-app share modal.
 *
 * Backend: POST /generate-share-copy → ShareApi.createGenerateShareCopy
 *
 * Returns punchier alternatives to the static `getSocialQuote()` templates.
 * Falls back server-side to a deterministic template when ANTHROPIC_API_KEY
 * is missing or the model call throws — frontend never sees an empty `copy`.
 */
import {useMutation} from "@tanstack/react-query";
import {ShareApi, PostGenerateShareCopyBody, GenerateShareCopyResponse} from "@devour/client";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";

/*
 * Re-export under stable local names so consumers don't need to track which
 * @devour/client model owns the shape — and so the hook can absorb future
 * shape evolutions in one place rather than requiring consumer updates.
 */
export type GenerateShareCopyInput = PostGenerateShareCopyBody;
export type GenerateShareCopyOutput = GenerateShareCopyResponse;

export function useGenerateShareCopy() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    return useMutation<GenerateShareCopyResponse, Error, PostGenerateShareCopyBody>({
        mutationFn: (postGenerateShareCopyBody) => {
            const api = new ShareApi(getConfig(fullToken));
            return api.createGenerateShareCopy({postGenerateShareCopyBody});
        },
    });
}
